/**
 * NotificationCard - Reusable clickable notification card component
 * 
 * Features:
 * - Entire card is clickable
 * - Hover state with cursor pointer
 * - Accessible: role="button", tabIndex=0, Enter/Space triggers open
 * - View button for explicit action
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Bell, Calendar, CreditCard, Shield, AlertTriangle, 
  FileText, CheckCircle, ExternalLink, ChevronRight
} from 'lucide-react';
import {
  resolveNotificationAction,
  getNotificationIconColor,
  buildNotificationUrl
} from '../utils/notificationDeepLink';

// Icon mapping
const IconMap = {
  Calendar,
  CreditCard,
  Shield,
  AlertTriangle,
  FileText,
  CheckCircle,
  Bell
};

const NotificationCard = ({ 
  notification, 
  onMarkRead, 
  currentUserRole = 'renter',
  compact = false 
}) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', body: '' });
  
  // Get icon based on notification type
  const getIcon = (type) => {
    if (type.includes("booking") || type.includes("calendar") || type.includes("trip") || type.includes("prehire") || type.includes("posthire") || type.includes("extension")) return Calendar;
    if (type.includes("payment") || type.includes("refund") || type.includes("charges")) return CreditCard;
    if (type.includes("verification") || type.includes("identity") || type.includes("licence")) return Shield;
    if (type.includes("dispute")) return AlertTriangle;
    if (type.includes("legal") || type.includes("terms")) return FileText;
    if (type === "welcome") return CheckCircle;
    return Bell;
  };
  
  const Icon = getIcon(notification.type);
  const iconColor = getNotificationIconColor(notification.type);
  
  // Handle click action
  const handleClick = async () => {
    // Mark as read if not already
    if (!notification.is_read && onMarkRead) {
      await onMarkRead(notification.notification_id);
    }
    
    // Resolve the action
    const action = resolveNotificationAction(notification, currentUserRole);
    
    if (action.kind === "navigate") {
      const url = buildNotificationUrl(action.route, action.params);
      navigate(url);
    } else if (action.kind === "modal") {
      setModalContent({
        title: action.title || notification.title,
        body: action.body || notification.message
      });
      setShowModal(true);
    }
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  if (compact) {
    return (
      <>
        <div 
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={`p-3 rounded-lg border transition-all cursor-pointer hover:shadow-md hover:border-[#004B8D]/30 focus:outline-none focus:ring-2 focus:ring-[#004B8D]/50 ${
            notification.is_read 
              ? 'bg-white border-slate-100' 
              : 'bg-blue-50/50 border-blue-100'
          }`}
          aria-label={`${notification.title} - Click to view details`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconColor}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-sm text-slate-900 truncate">{notification.title}</h4>
                {!notification.is_read && (
                  <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                )}
              </div>
              <p className="text-xs text-slate-500 truncate">{notification.message}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </div>
        </div>
        
        {/* Detail Modal for fallback */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-white border-slate-200 shadow-xl">
            <DialogHeader>
              <DialogTitle>{modalContent.title}</DialogTitle>
              <DialogDescription>{modalContent.body}</DialogDescription>
            </DialogHeader>
            <div className="text-xs text-slate-400 mt-2">
              {formatDate(notification.created_at)}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  
  return (
    <>
      <div 
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`p-4 rounded-xl border transition-all cursor-pointer hover:shadow-md hover:border-[#004B8D]/30 focus:outline-none focus:ring-2 focus:ring-[#004B8D]/50 group ${
          notification.is_read 
            ? 'bg-white border-slate-100' 
            : 'bg-blue-50/50 border-blue-100'
        }`}
        aria-label={`${notification.title} - Click to view details`}
      >
        <div className="flex gap-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconColor}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-slate-900">{notification.title}</h4>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!notification.is_read && (
                  <Badge className="bg-blue-100 text-blue-800">New</Badge>
                )}
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  View
                </Button>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-1">{notification.message}</p>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
              {notification.email_to && <span>To: {notification.email_to}</span>}
              <span>{formatDate(notification.created_at)}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Detail Modal for fallback */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-white border-slate-200 shadow-xl">
          <DialogHeader>
            <DialogTitle>{modalContent.title}</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{modalContent.body}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between text-xs text-slate-400 mt-4 pt-4 border-t">
            <span>{formatDate(notification.created_at)}</span>
            {notification.email_to && <span>Sent to: {notification.email_to}</span>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationCard;
